'use client'

import { compactClassName } from '@/components/homepage-october/compact'
import * as React from 'react'
import { ChevronLeft, ChevronRight } from '@/components/homepage-october/lucide'

import { Button } from '@/components/homepage-october/ui/button'
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/homepage-october/ui/carousel'
import { cn } from '@/lib/utils'
import { useDragScroll, useReducedMotion } from '../hooks'
import type { Link, Product } from '../types'
import { ViewAll } from './layout'
import { ProductCard } from './ProductCard'

/** Previous/next buttons wired to an Embla api (the stock shadcn carousel buttons, placed in the header). */
export function RailNav({ api, label, className, onNavigate }: { api: CarouselApi | undefined; label: string; className?: string; onNavigate?: () => void }) {
  // Read Embla as the external store it is (the same pattern Repo's carousel uses).
  const subscribe = React.useCallback((cb: () => void) => {
    if (!api) return () => {}
    api.on('select', cb).on('reInit', cb)
    return () => { api.off('select', cb).off('reInit', cb) }
  }, [api])
  const prev = React.useSyncExternalStore(subscribe, () => (api ? api.canScrollPrev() : false), () => false)
  const next = React.useSyncExternalStore(subscribe, () => (api ? api.canScrollNext() : true), () => true)
  return (
    <div className={compactClassName(cn('flex shrink-0 items-center gap-2', className))}>
      <Button type="button" variant="outline" size="icon" className="size-11! rounded-full! bg-white" disabled={!prev} onClick={() => { onNavigate?.(); api?.scrollPrev() }} aria-label={`Previous: ${label}`}>
        <ChevronLeft aria-hidden="true" />
      </Button>
      <Button type="button" variant="outline" size="icon" className="size-11! rounded-full! bg-white" disabled={!next} onClick={() => { onNavigate?.(); api?.scrollNext() }} aria-label={`Next: ${label}`}>
        <ChevronRight aria-hidden="true" />
      </Button>
    </div>
  )
}

/**
 * A row of product cards on the shadcn Carousel (Embla). `autoplay` steps the row every few seconds and
 * pauses on hover and keyboard focus. Direct navigation stops autoplay for the rest of this visit.
 */
export function ProductRail({ products, title, sub, viewAll, id, autoplay, level = 3, kicker, basis, className, eagerFirst }: {
  products: Product[]
  title?: string
  sub?: string
  viewAll?: Link | null
  id?: string
  autoplay?: number
  level?: 2 | 3
  kicker?: React.ReactNode
  basis?: string
  className?: string
  eagerFirst?: number
}) {
  const [api, setApi] = React.useState<CarouselApi>()
  const reduced = useReducedMotion()
  const [paused, setPaused] = React.useState(false)
  const hold = React.useRef({ hover: false, focus: false, visible: false })
  React.useEffect(() => {
    if (!api) return
    const observer = new IntersectionObserver(([entry]) => { hold.current.visible = entry.isIntersecting }, { threshold: 0.15 })
    observer.observe(api.rootNode())
    const stop = () => setPaused(true)
    api.on('pointerDown', stop)
    return () => { observer.disconnect(); api.off('pointerDown', stop) }
  }, [api])
  React.useEffect(() => {
    if (!api || !autoplay || reduced || paused) return
    const t = window.setInterval(() => {
      if (hold.current.hover || hold.current.focus || !hold.current.visible || document.hidden) return
      if (api.canScrollNext()) api.scrollNext()
      else api.scrollTo(0)
    }, autoplay)
    return () => window.clearInterval(t)
  }, [api, autoplay, reduced, paused])
  const H = level === 2 ? 'h2' : 'h3'
  const label = title ?? 'products'
  if (products.length === 0) return null
  return (
    <Carousel
      data-auto={autoplay || undefined}
      data-product-rail
      data-paused={paused || reduced}
      setApi={setApi}
      opts={{ align: 'start', containScroll: 'trimSnaps', slidesToScroll: 'auto', watchDrag: true, duration: reduced ? 0 : 20 }}
      aria-label={label}
      // contain:inline-size: the row never widens its grid/flex parent to fit every card (35 cards ≈ 9,000 px)
      className={cn('w-full min-w-0 [contain:inline-size]', className)}
      onMouseEnter={() => { hold.current.hover = true }}
      onMouseLeave={() => { hold.current.hover = false }}
      onFocusCapture={() => { hold.current.focus = true }}
      onKeyDown={(e) => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') setPaused(true) }}
      onBlurCapture={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) hold.current.focus = false }}
    >
      {title || viewAll ? (
        <div className={compactClassName("mb-4 flex flex-wrap items-end justify-between gap-x-6 gap-y-2")}>
          <div className={compactClassName("min-w-0 max-w-3xl")}>
            {kicker}
            {title ? <H id={id} className={compactClassName(cn('font-semibold tracking-tight text-neutral-950', level === 2 ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl'))}>{title}</H> : null}
            {sub ? <p className={compactClassName("mt-1.5 text-pretty text-sm leading-relaxed text-neutral-600 md:text-base")}>{sub}</p> : null}
          </div>
          <div className={compactClassName("flex items-center gap-3")}>
            {viewAll ? <ViewAll link={viewAll} variant="link" className="hidden! sm:inline-flex!" /> : null}
            <RailNav api={api} label={label} onNavigate={() => setPaused(true)} />
          </div>
        </div>
      ) : null}
      <CarouselContent className="pb-1">
        {products.map((p, i) => (
          <CarouselItem key={p.fin} className={cn('basis-[72%] sm:basis-[44%] md:basis-1/3 lg:basis-1/4 xl:basis-1/5 min-[1700px]:basis-1/6 min-[2100px]:basis-[14.2857%]', basis)}>
            <ProductCard p={p} headingLevel={level === 2 ? 3 : 4} eager={eagerFirst != null && i < eagerFirst} />
          </CarouselItem>
        ))}
      </CarouselContent>
      {viewAll ? <ViewAll link={viewAll} variant="link" className="mt-4 sm:hidden!" /> : null}
    </Carousel>
  )
}

/**
 * A continuously moving strip (ticker, press logos, pins). The copy that makes the loop seamless is hidden
 * from assistive tech and from the tab order. Hover or focus pauses it; reduced motion stops it.
 */
export function Marquee({ children, duration = 60, className, trackClassName, label, reverse, still = 'wrap' }: {
  children: React.ReactNode
  duration?: number
  className?: string
  trackClassName?: string
  label?: string
  reverse?: boolean
  /** Under reduced motion: 'wrap' lays the items out as a still, wrapped row (logos, ribbons); 'scroll' keeps one
   *  swipeable row, for strips made of links so the keyboard can still reach every item. */
  still?: 'wrap' | 'scroll'
}) {
  const viewport = React.useRef<HTMLDivElement>(null)
  const track = React.useRef<HTMLDivElement>(null)
  const viewportId = React.useId()
  const [manual, setManual] = React.useState(false)
  const stopped = React.useRef(false)
  const takeover = () => {
    if (stopped.current || !viewport.current || !track.current) return
    stopped.current = true
    const el = track.current
    const transform = getComputedStyle(el).transform
    const offset = transform === 'none' ? 0 : -new DOMMatrixReadOnly(transform).m41
    // Preserve the currently visible position when changing from animation to native scrolling.
    el.style.animation = 'none'
    el.style.transform = 'none'
    const copy = el.children[1] as HTMLElement | undefined
    if (copy) copy.style.display = 'none'
    viewport.current.style.overflowX = 'auto'
    viewport.current.scrollLeft = Math.max(0, offset)
    setManual(true)
  }
  const drag = useDragScroll(viewport, takeover)
  return (
    <div ref={viewport} {...drag} id={viewportId} role="region" aria-label={label || 'Latest price drops'} data-manual={manual} data-still={still}
      onFocusCapture={takeover}
      onWheelCapture={(e) => { if (e.deltaX) takeover() }}
      className={compactClassName(cn('v3-marquee v3-noscrollbar relative overflow-hidden outline-offset-4 focus-within:outline-2 focus-within:outline-[#0454c0]', className))}>
      <button type="button" className={compactClassName("sr-only")} aria-label={`Scroll left or right: ${label || 'Latest price drops'}`} aria-controls={viewportId}
        onClick={takeover}
        onKeyDown={(e) => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); takeover(); viewport.current?.scrollBy({ left: (e.key === 'ArrowRight' ? 1 : -1) * viewport.current.clientWidth * .7, behavior: 'instant' }) } }} />
      <div
        ref={track}
        data-motion
        className={compactClassName(cn('v3-marquee-track flex w-max animate-v3-marquee', reverse && '[animation-direction:reverse]', trackClassName))}
        style={{ '--v3-marquee-duration': `${duration}s` } as React.CSSProperties}
      >
        <div className={compactClassName("flex shrink-0 items-center")}>{children}</div>
        <div className={compactClassName("flex shrink-0 items-center")} style={manual ? { display: 'none' } : undefined} aria-hidden="true" inert>{children}</div>
      </div>
    </div>
  )
}
